import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { workshopService } from "@/services/workshop.service";
import { getErrorMessage } from "@/services/api";
import {
    WORKSHOP_FAILURE_TYPES,
    WORKSHOP_GENERAL_CONDITIONS,
} from "@/config/constants";

const PRIORITIES = [
    { value: "baixa", label: "Baixa" },
    { value: "media", label: "Média" },
    { value: "alta", label: "Alta" },
    { value: "critica", label: "Crítica" },
];

function Row({ children }) { return <div className="grid gap-3 md:grid-cols-2">{children}</div>; }
function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium">{label}</Label>
            {children}
        </div>
    );
}

export function DiagnosisForm({ workshop }) {
    const qc = useQueryClient();
    const readOnly = workshop?.workshop_status === "aguardando_proposta";
    const disabled = readOnly || workshop?.workshop_status === "aguardando_recebimento";

    const d = workshop?.diagnosis || {};
    const l = workshop?.labor_estimation || {};

    const { register, handleSubmit, watch, setValue, reset, formState: { isDirty } } = useForm({
        defaultValues: {
            failure_type: d.failure_type || "",
            failure_cause: d.failure_cause || "",
            detailed_description: d.detailed_description || "",
            equipment_general_condition: d.equipment_general_condition || "",
            technical_recommendation: d.technical_recommendation || "",
            estimated_repair_hours: d.estimated_repair_hours ?? "",
            estimated_repair_days: d.estimated_repair_days ?? "",
            priority: d.priority || l.priority || "",
            observations: d.observations || "",
        },
    });

    useEffect(() => {
        reset({
            failure_type: d.failure_type || "",
            failure_cause: d.failure_cause || "",
            detailed_description: d.detailed_description || "",
            equipment_general_condition: d.equipment_general_condition || "",
            technical_recommendation: d.technical_recommendation || "",
            estimated_repair_hours: d.estimated_repair_hours ?? "",
            estimated_repair_days: d.estimated_repair_days ?? "",
            priority: d.priority || l.priority || "",
            observations: d.observations || "",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workshop?.id]);

    const save = useMutation({
        mutationFn: async (payload) => {
            const diagnosisPayload = {
                failure_type: payload.failure_type || null,
                failure_cause: payload.failure_cause || null,
                detailed_description: payload.detailed_description || null,
                equipment_general_condition: payload.equipment_general_condition || null,
                technical_recommendation: payload.technical_recommendation || null,
                estimated_repair_hours: payload.estimated_repair_hours !== "" ? Number(payload.estimated_repair_hours) : null,
                estimated_repair_days: payload.estimated_repair_days !== "" ? Number(payload.estimated_repair_days) : null,
                priority: payload.priority || null,
                observations: payload.observations || null,
            };
            await workshopService.updateDiagnosis(workshop.id, diagnosisPayload);
            // Sync labor estimation from the same form
            await workshopService.updateLaborEstimation(workshop.id, {
                estimated_hours: diagnosisPayload.estimated_repair_hours,
                estimated_days: diagnosisPayload.estimated_repair_days,
                priority: diagnosisPayload.priority,
            });
        },
        onSuccess: () => {
            toast.success("Diagnóstico salvo.");
            qc.invalidateQueries({ queryKey: ["workshop", workshop.id] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao salvar diagnóstico.")),
    });

    const canStart = workshop?.workshop_status === "recebido";
    const startDiagnosis = useMutation({
        mutationFn: () => workshopService.startDiagnosis(workshop.id),
        onSuccess: () => {
            toast.success("Diagnóstico iniciado.");
            qc.invalidateQueries({ queryKey: ["workshop", workshop.id] });
            qc.invalidateQueries({ queryKey: ["workshop-dashboard"] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao iniciar diagnóstico.")),
    });

    return (
        <form onSubmit={handleSubmit(save.mutate)} className="space-y-4" data-testid="diagnosis-form">
            {canStart && (
                <div className="flex items-center justify-between gap-2 rounded-md border border-orange-500/40 bg-orange-50 p-3 dark:bg-orange-950/30">
                    <div className="text-xs text-orange-800 dark:text-orange-200">
                        Equipamento recebido. Inicie o diagnóstico para liberar a edição dos campos técnicos.
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        onClick={() => startDiagnosis.mutate()}
                        disabled={startDiagnosis.isPending}
                        data-testid="start-diagnosis-button"
                    >
                        Iniciar diagnóstico
                    </Button>
                </div>
            )}

            <Row>
                <Field label="Tipo de falha">
                    <Select value={watch("failure_type") || ""} onValueChange={(v) => setValue("failure_type", v, { shouldDirty: true })} disabled={disabled}>
                        <SelectTrigger data-testid="diag-failure-type"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                        <SelectContent>
                            {WORKSHOP_FAILURE_TYPES.map((f) => (
                                <SelectItem key={f} value={f}>{f}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Causa da falha">
                    <Input {...register("failure_cause")} disabled={disabled} data-testid="diag-failure-cause" />
                </Field>
            </Row>

            <Field label="Descrição técnica detalhada">
                <Textarea rows={4} {...register("detailed_description")} disabled={disabled} data-testid="diag-description" />
            </Field>

            <Row>
                <Field label="Condição geral do equipamento">
                    <Select value={watch("equipment_general_condition") || ""} onValueChange={(v) => setValue("equipment_general_condition", v, { shouldDirty: true })} disabled={disabled}>
                        <SelectTrigger data-testid="diag-general-condition"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                        <SelectContent>
                            {WORKSHOP_GENERAL_CONDITIONS.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Recomendação técnica">
                    <Input {...register("technical_recommendation")} disabled={disabled} placeholder="Ex: Reparo completo do rotor" data-testid="diag-technical-reco" />
                </Field>
            </Row>

            <Row>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Tempo estimado (horas)">
                        <Input type="number" step="0.5" {...register("estimated_repair_hours")} disabled={disabled} data-testid="diag-hours" />
                    </Field>
                    <Field label="Tempo estimado (dias)">
                        <Input type="number" step="0.5" {...register("estimated_repair_days")} disabled={disabled} data-testid="diag-days" />
                    </Field>
                </div>
                <Field label="Prioridade">
                    <Select value={watch("priority") || ""} onValueChange={(v) => setValue("priority", v, { shouldDirty: true })} disabled={disabled}>
                        <SelectTrigger data-testid="diag-priority"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                        <SelectContent>
                            {PRIORITIES.map((p) => (
                                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
            </Row>

            <Field label="Observações">
                <Textarea rows={2} {...register("observations")} disabled={disabled} data-testid="diag-observations" />
            </Field>

            <div className="flex justify-end">
                <Button
                    type="submit"
                    disabled={disabled || save.isPending || !isDirty}
                    className="bg-secondary text-secondary-foreground"
                    data-testid="diag-save-button"
                >
                    <Save className="mr-2 h-4 w-4" /> Salvar diagnóstico
                </Button>
            </div>
        </form>
    );
}

export default DiagnosisForm;
